import { log } from '@hooks/logger';

import { LogLevel } from '@typings/graphql';

const isLeapYear = year =>
  (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

const getDaysInMonth = (year, monthIndex) => {
  const daysByMonth = [
    31,
    isLeapYear(year) ? 29 : 28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31,
  ];
  return daysByMonth[monthIndex] ?? 0;
};

const isValidDateParts = (year, monthIndex, day) => {
  if (
    !Number.isInteger(year) ||
    !Number.isInteger(monthIndex) ||
    !Number.isInteger(day)
  )
    return false;
  if (monthIndex < 0 || monthIndex > 11) return false;
  return day >= 1 && day <= getDaysInMonth(year, monthIndex);
};

const isValidTimeParts = (hour, minute, second) => {
  if (
    !Number.isInteger(hour) ||
    !Number.isInteger(minute) ||
    !Number.isInteger(second)
  )
    return false;
  if (hour < 0 || hour > 23) return false;
  if (minute < 0 || minute > 59) return false;
  return second >= 0 && second <= 59;
};

const parseDateParts = value => {
  if (!value) return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return {
      year: value.getUTCFullYear(),
      monthIndex: value.getUTCMonth(),
      day: value.getUTCDate(),
      hour: value.getUTCHours(),
      minute: value.getUTCMinutes(),
      second: value.getUTCSeconds(),
      hasTime: true,
    };
  }

  const input = String(value).trim();
  let match = input.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/);
  if (match) {
    const year = parseInt(match[1], 10);
    const monthIndex = parseInt(match[2], 10) - 1;
    const day = parseInt(match[3], 10);
    const hour = parseInt(match[4], 10);
    const minute = parseInt(match[5], 10);
    const second = parseInt(match[6], 10);
    if (
      !isValidDateParts(year, monthIndex, day) ||
      !isValidTimeParts(hour, minute, second)
    )
      return null;
    return { year, monthIndex, day, hour, minute, second, hasTime: true };
  }

  match = input.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d{1,3})?Z)?$/
  );
  if (match) {
    const year = parseInt(match[1], 10);
    const monthIndex = parseInt(match[2], 10) - 1;
    const day = parseInt(match[3], 10);
    const hasTime = Boolean(match[4]);
    const hour = hasTime ? parseInt(match[4], 10) : 0;
    const minute = hasTime ? parseInt(match[5], 10) : 0;
    const second = hasTime && match[6] ? parseInt(match[6], 10) : 0;
    if (
      !isValidDateParts(year, monthIndex, day) ||
      (hasTime && !isValidTimeParts(hour, minute, second))
    )
      return null;
    return { year, monthIndex, day, hour, minute, second, hasTime };
  }

  return null;
};

const warnInvalidAnnouncementDate = value => {
  const isProduction =
    typeof process !== 'undefined' &&
    process.env &&
    process.env.NODE_ENV === 'production';
  if (isProduction) return;

  log({
    level: LogLevel.Warn,
    message: `formatAnnouncementDate: invalid date input, ${value}`,
  });
};

export function formatAnnouncementDate(dateInput, lang = 'en') {
  if (!dateInput) return '';

  let dateValue = dateInput;
  if (
    !(dateInput instanceof Date) &&
    typeof dateInput === 'object' &&
    dateInput.value
  ) {
    dateValue = dateInput.value;
  }

  if (!dateValue) return '';

  const dateParts = parseDateParts(dateValue);
  if (!dateParts) {
    warnInvalidAnnouncementDate(dateValue);
    return '';
  }

  const utcDate = new Date(
    Date.UTC(dateParts.year, dateParts.monthIndex, dateParts.day)
  );

  const langLocaleMap = {
    en: 'en-us',
    de: 'de-de',
    fr: 'fr-fr',
    it: 'it-it',
    ja: 'ja-jp',
    ko: 'ko-kr',
    pt: 'pt-br',
    es: 'es-419',
    // Legacy Sitecore language code still present in content.
    ez: 'es-419',
  };

  const localeMap = {
    // Keep legacy UX behavior: English dates are intentionally rendered in en-au style.
    'en-us': 'en-au',
    'de-de': 'de-de',
    'fr-fr': 'fr-fr',
    'it-it': 'it-it',
    'ja-jp': 'ja-jp',
    'ko-kr': 'ko-kr',
    'pt-br': 'pt-br',
    'es-419': 'es-419',
  };

  const normalizedLang =
    typeof lang === 'string' ? lang.trim().toLowerCase() : 'en';
  const safeLang = normalizedLang || 'en';
  const localeKey = langLocaleMap[safeLang] ?? safeLang;
  const locale = localeMap[localeKey] ?? localeKey;
  const options = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  };
  let formatter;
  try {
    formatter = new Intl.DateTimeFormat(locale, options);
  } catch (error) {
    formatter = new Intl.DateTimeFormat('en-au', options);
  }

  if (localeKey === 'pt-br') {
    // Remove connector literals ("de") safely without mutating month/day tokens.
    return formatter
      .formatToParts(utcDate)
      .filter(
        part =>
          !(part.type === 'literal' && part.value.trim().toLowerCase() === 'de')
      )
      .map(part => part.value)
      .join('')
      .replace(/\s+/g, ' ')
      .trim();
  }

  return formatter.format(utcDate);
}
